/**
 * Types for AI Content Generator components
 */

import type { ReactNode, CSSProperties, ComponentType } from 'react';
import type {
    JobDetail,
    GenerateContentRequest,
    GenerateContentFromDocumentRequest,
    RegenerateContentRequest,
} from '@ai-content-hub/shared-api';

export type EditorMode = 'edit' | 'ai-prompt';

/**
 * EAA-2026: a tenant question-type group as the prompt form reads it — structurally the
 * content-prompt ``QuestionTypeGroupInput`` (kept local to avoid a new package import cycle).
 */
export interface QuestionTypeGroupInput {
    id: string;
    name: string;
    ai_content_types: Array<{ id: string; slug: string }>;
}

/** Theme colors for AI Generator components */
export interface AIGeneratorColors {
    primary: string;
    cta: string;
    ctaHover: string;
    background: string;
    surface: string;
    text: string;
    textMuted: string;
    border: string;
    error: string;
    success: string;
    info: string;
}

/** Default colors using CSS variables (for shared packages) */
export const DEFAULT_COLORS: AIGeneratorColors = {
    primary: 'var(--color-primary, #1890ff)',
    cta: 'var(--color-cta, #007FA3)',
    ctaHover: 'var(--color-cta-hover, #006888)',
    background: 'var(--color-background, #ffffff)',
    surface: 'var(--color-surface, #f5f5f5)',
    text: 'var(--color-text, #1f2937)',
    textMuted: 'var(--color-text-muted, #6b7280)',
    border: 'var(--color-border, #e5e7eb)',
    error: 'var(--color-error, #ff4d4f)',
    success: 'var(--color-success, #52c41a)',
    info: 'var(--color-info, #1890ff)',
};

/**
 * Data returned by a prompt form on submit.
 * Mirrors content-prompt PromptFormData so shared-components stays aligned.
 */
export interface PromptFormData {
    prompt: string;
    metadata?: Record<string, unknown>;
    option?: Record<string, unknown>;
    /** EAA-1739: initial Content.status to save as (e.g. 2=draft, 3=published) */
    status?: number;
}

/** API config for document upload in prompt forms */
export interface PromptFormApiConfig {
    baseUrl: string;
    getAuthToken?: () => string | null;
}

/**
 * Props contract for injected prompt form components (e.g. from content-kit).
 */
export interface PromptFormProps {
    questionType: string;
    onSubmit: (data: PromptFormData) => void;
    onCancel?: () => void;
    loading?: boolean;
    disabled?: boolean;
    isRegeneration?: boolean;
    className?: string;
    apiConfig?: PromptFormApiConfig;
    questionTypeOptions?: QuestionTypeOption[];
    /** All AI content type options from API (for Quick Create dialog in PromptFormRenderer) */
    allQuestionTypeOptions?: QuestionTypeOption[];
    /** EAA-2026: tenant question-type groups (catalog ``groups``) heading the Quick Create list */
    questionTypeGroups?: QuestionTypeGroupInput[];
    /** Callback to change the active question type (used by Quick Create) */
    onQuestionTypeChange?: (slug: string) => void;
    /** When true, Quick Create is hosted outside the prompt form (e.g. page header) */
    hideQuickCreateUI?: boolean;
    /** Job status summary for inline progress (from AIContentGenerator) */
    jobStatus?: unknown | null;
    /** Retry callback when job failed (primary button becomes Retry) */
    onRetry?: () => void;
    /** Dismiss callback for terminal job state */
    onDismiss?: () => void;
}

/** Ref for imperative control of AIContentGenerator (e.g. trigger generate from parent) */
export interface AIContentGeneratorRef {
    generate(options?: { prompt?: string; questionType?: string }): Promise<void>;
}

/** API functions for content version history (injected to keep shared-components independent) */
export interface GenerationHistoryApi {
    listVersions: (contentId: string) => Promise<
        Array<{
            id: string;
            content_id: string;
            version: number;
            prompt: string;
            metadata: Record<string, unknown> | null;
            job_id: string | null;
            is_current: boolean;
            created_at: string;
        }>
    >;
    rollback: (
        contentId: string,
        version: number
    ) => Promise<{ job_id?: string; content_id?: string; version?: number }>;
}

export interface AIContentGeneratorProps {
    /** Pre-selected question type slug (optional) */
    questionType?: string;
    /** Override label for question type selector (when not hidden) */
    questionTypeLabel?: string;
    /** Override placeholder for question type selector (when not hidden) */
    questionTypePlaceholder?: string;
    /** When true, do not render the question type selector in the form (selector is in parent, e.g. header) */
    hideQuestionTypeSelector?: boolean;
    /** When true, do not render Quick Create inside the prompt form (Quick Create is in parent header) */
    hideQuickCreateUI?: boolean;
    /** Tenant ID for multi-tenant context */
    tenantId?: string;
    /** Existing content ID for regeneration (updates content instead of creating new) */
    contentId?: string;
    /** localStorage key for job persistence (default: 'ai-job-tracking') */
    storageKey?: string;
    /** Whether to show the preview panel (split view) */
    showPreview?: boolean;
    /** Content to render in the preview panel (ReactNode) */
    previewContent?: ReactNode;
    /** Title for the preview panel header */
    previewTitle?: string;
    /** Callback to refresh the preview content */
    onRefreshPreview?: () => void;
    /** Callback when job is created */
    onJobCreated?: (jobId: string) => void;
    /** Callback when job completes successfully */
    onJobCompleted?: (result: JobDetail) => void;
    /** Callback when an error occurs */
    onError?: (error: string) => void;
    /** Polling interval in ms (default: 2000) */
    pollingInterval?: number;
    /** Theme colors (optional, uses defaults if not provided) */
    colors?: Partial<AIGeneratorColors>;
    /** Additional CSS class name */
    className?: string;
    /** Custom prompt form component (injected from content-kit). Falls back to built-in PromptInput when not provided. */
    promptFormComponent?: ComponentType<PromptFormProps>;
    /** API config for document upload (passed to prompt form when supported) */
    apiConfig?: PromptFormApiConfig;
    /** Injected API for content version history (enables chat mode after first generation) */
    generationHistoryApi?: GenerationHistoryApi;
}

export interface UseJobTrackingOptions {
    /** localStorage key (default: 'ai-job-tracking') */
    storageKey?: string;
    /** ms between polls (default: 2000) */
    pollingInterval?: number;
    /** max attempts before timeout (default: 150 = 5 min) */
    maxPollingAttempts?: number;
    /** Existing content ID for regeneration */
    contentId?: string;
    /** Callback when job completes */
    onJobCompleted?: (job: JobDetail) => void;
    /** Callback when job fails */
    onJobFailed?: (error: string) => void;
}

export interface UseJobTrackingReturn {
    // State
    jobId: string | null;
    jobStatus: JobDetail | null;
    isGenerating: boolean;
    isPolling: boolean;
    error: string | null;
    /** Whether regeneration mode (contentId provided) */
    isRegeneration: boolean;

    // Actions
    startGeneration: (request: GenerateContentRequest) => Promise<void>;
    startGenerationFromDocument: (
        request: GenerateContentFromDocumentRequest
    ) => Promise<void>;
    startRegeneration: (request: RegenerateContentRequest) => Promise<void>;
    /** Start polling an existing job (e.g. from chat regeneration) */
    trackJob: (jobId: string) => void;
    syncJobStatus: () => Promise<void>;
    clearJob: () => void;
    cancelPolling: () => void;
}

export interface PromptInputProps {
    /** Current prompt value */
    value: string;
    /** Callback when prompt changes */
    onChange: (value: string) => void;
    /** Callback when send button is clicked */
    onSend: () => void;
    /** Whether the input is disabled */
    disabled?: boolean;
    /** Whether currently sending/generating */
    loading?: boolean;
    /** Whether in regeneration mode (shows "Regenerate" instead of "Generate") */
    isRegeneration?: boolean;
    /** Placeholder text */
    placeholder?: string;
    /** Maximum character count (default: 5000) */
    maxLength?: number;
    /** Theme colors */
    colors?: Partial<AIGeneratorColors>;
    /** Additional CSS class name */
    className?: string;
}

export interface JobProgressProps {
    /** Current job status */
    jobStatus: JobDetail | null;
    /** Whether currently polling */
    isPolling?: boolean;
    /** Callback to retry failed job */
    onRetry?: () => void;
    /** Callback to dismiss/clear job */
    onDismiss?: () => void;
    /** Theme colors */
    colors?: Partial<AIGeneratorColors>;
    /** Additional CSS class name */
    className?: string;
}

export interface QuestionTypeSelectorProps {
    /** Current selected question type slug */
    value?: string;
    /** Callback when selection changes - passes slug only (backend auto-resolves content_type_id) */
    onChange: (slug: string) => void;
    /** Whether the selector is disabled */
    disabled?: boolean;
    /** Placeholder text */
    placeholder?: string;
    /** Theme colors */
    colors?: Partial<AIGeneratorColors>;
    /** Additional CSS class name */
    className?: string;
    /** Inline styles for the root element (e.g. width for layout consistency) */
    style?: CSSProperties;
    /** Pre-fetched options (when provided, selector won't fetch internally) */
    options?: QuestionTypeOption[];
    /** Loading state from parent */
    loading?: boolean;
    /** Error from parent */
    error?: string | null;
}

export interface QuestionTypeOption {
    id: string;
    slug: string;
    /** Hub AIContentType.code — used by Quick Create allowlist. */
    code?: string;
    name: string;
    description?: string;
    category?: string;
}
